import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });

  useEffect(() => {
    // Check if already logged in with a valid token
    const validateExistingToken = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          // Validate the token
          await axios.get(`${API}/admin/system/config`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Token is valid, redirect to dashboard
          navigate('/admin/dashboard');
        } catch (error) {
          // Token is invalid, remove it
          localStorage.removeItem('admin_token');
        }
      }
    };
    
    validateExistingToken();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Clear any old token before login attempt
    localStorage.removeItem('admin_token');

    try {
      const response = await axios.post(`${API}/auth/admin/login`, formData);
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('Admin-Login erfolgreich!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültige Zugangsdaten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
            <Shield className="w-10 h-10 text-gray-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Administrator</h1>
          <p className="text-gray-600">Zugang zum Verwaltungsbereich</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username" className="text-sm font-semibold text-gray-700 pl-1">
              Benutzername
            </Label>
            <Input
              id="username"
              type="text"
              data-testid="admin-username-input"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              className="w-full h-12 text-base border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all bg-white shadow-sm"
              placeholder="Benutzername"
            />
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-sm font-semibold text-gray-700 pl-1">
              Passwort
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="w-full h-12 text-base border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all bg-white shadow-sm"
              placeholder="Passwort"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all mt-6"
            disabled={loading}
            data-testid="admin-login-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Anmelden...
              </>
            ) : (
              'Anmelden'
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <a 
            href="/teilnehmer/login" 
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            ← Zurück zum Teilnehmer-Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
